"use client";

import { createContext, useContext, useState, ReactNode } from "react";

type Language = "KOR" | "EN";

interface LanguageContextType {
  currentLang: Language;
  setCurrentLang: (lang: Language) => void;
  translations: {
    KOR: any;
    EN: any;
  };
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

const translations = {
  KOR: {
    logo: "PUMASY AI",
    menu: [
      "회사소개",
      "기술",
      "소식",
      "고객지원",
      "온라인 청약 신청",
      "공지사항",
    ],
    subLeft: "성장",

    subRight: "특이점",
    desc: "끊임없는 연구개발을 통해 인공지능 기술의 혁신을 선도합니다.",
    aboutTitle: "회사소개",
    aboutOverviewTitle: "사업의 개요",
    aboutOverviewDesc:
      "놀라운 속도로 진화하는 인공지능 시대에 맞추어, PUMASY AI는 사용자 참여 기반의 고도화된 LLM 학습 구조로 차별화된 경쟁력을 확보합니다. 사용자가 보상으로 참여하여 생성한 고품질 데이터를 선별·학습함으로써 환각(hallucination) 문제를 집단지성으로 검증·개선하고, 반복 학습으로 인한 정보 왜곡을 예방합니다. 또한, 저작권 기반의 경제 생태계와 광고 수익 공유 모델을 통하여 지속 가능한 플랫폼 성장을 추구합니다.",
    aboutDesc1:
      "PUMASY AI는 차세대 인공지능 기술을 통해 미래를 혁신하는 선도적인 AI 플랫폼 기업입니다.",
    aboutDesc2:
      "우리는 기업과 개인이 AI의 힘을 활용하여 더 나은 결과를 달성할 수 있도록 돕는 것을 목표로 합니다.",
    visionTitle: "비전",
    visionDesc: "인공지능을 통해 인류의 삶을 더욱 풍요롭게 만드는 것",
    missionTitle: "미션",
    missionDesc: "혁신적인 AI 솔루션으로 비즈니스와 일상의 문제를 해결",
    valuesTitle: "가치",
    valuesDesc: "신뢰성, 혁신성, 그리고 사용자 중심의 접근 방식",
    techTitle: "기술",
    techDesc1:
      "PUMASY AI는 기존의 머신러닝과 딥러닝보다 훨씬 더 정교한 핵심심화학습 모델(Core Deep Learning)을 자체 개발하여, AI 특이점 경쟁에서 우위를 확보하고 지속해서 통제 가능한 AI 개발을 목표로  합니다.",
    techProductsTitle: "주요 제품 및 서비스",
    techProductsDesc:
      "푸마시가 개발하는 제품과 서비스는 모델 개발 도구, 환각 오류 처리 시스템, 차별화된 학습 파이프라인, 통합 콘텐츠 제작툴, 경제 생태계 및 B2B 데이터 자산화로 구성됩니다.",
    product1Title: "모델 개발 도구",
    product1Desc:
      "데이터 전처리, 파인튜닝 모듈, 서비스 배포 모듈 등 푸마시에 특화된 모델 개발 툴을 통해 생산성 높은 AI 개발 환경을 제공합니다.",
    product2Title: "환각 오류 처리 시스템",
    product2Desc:
      "SearchGPT로 광고 제거된 상위 검색 결과를 요약하고, ReportCopilot을 통한 사용자 검증으로 환각을 발견·교정하는 정교한 검증 체계를 시험중입니다.",
    product3Title: "차별화된 학습 파이프라인",
    product3Desc:
      "사용자 참여 보상 시스템으로 다채로운 생성 콘텐츠를 수집·선별하여 상위 샘플을 학습시키는 일반-추론 하이브리드 구조로 추론 성능을 향상합니다.",
    product4Title: "콘텐츠 통합 스튜디오",
    product4Desc:
      "이미지, 영상, 음악 제작을 통합 처리하는 도구를 개발하여 창작자가 수익형 콘텐츠를 손쉽게 제작할 수 있도록 최적화합니다.",
    product5Title: "경제 생태계 및 저작권 관리",
    product5Desc:
      "사용자에게 창작 소유권 개념을 도입해 보상과 저작권을 보장하며, 플랫폼 내 경제적 순환을 가능하게 합니다.",
    product6Title: "B2B 솔루션 및 제휴 마케팅",
    product6Desc:
      "푸마시의 핵심 데이터와 맞춤형 AI 서비스를 통해 제휴사의 사업을 글로벌로 확장하고, 클라우드 기반 빅데이터 자산화로 수익을 창출합니다.",
    nlpTitle: "Instruction - Following",
    nlpDesc: "사용자의 의도에 부합하는 답변 생성",
    cvTitle: "Reward Model",
    cvDesc: "생성한 답변에 대한 사람의 피드백 점수를 반영함",
    mlTitle: "머신러닝 플랫폼",
    mlDesc:
      "NoCode/LowCode 환경으로 누구나 쉽게 AI 모델에 대한 접근성을 높였습니다.",
    automlTitle: "AutoML",
    automlDesc:
      "자동화된 기계학습으로 최적의 모델을 빠르게 개발할 수 있습니다.",
    newsTitle: "소식",
    newsDesc: "PUMASY AI의 최신 소식과 업데이트를 확인하세요.",
    news1Title: "PUMASY AI, 차세대 멀티모달 AI 플랫폼 출시",
    news1Desc:
      "텍스트, 이미지, 음성을 통합 처리하는 혁신적인 AI 솔루션을 공개했습니다.",
    news2Title: "글로벌 파트너십 확대",
    news2Desc: "북미 및 유럽 시장 진출을 위한 전략적 파트너십을 체결했습니다.",
    news3Title: "AI 안전성 인증 획득",
    news3Desc:
      "국제 AI 안전성 표준을 충족하는 인증을 획득하여 신뢰성을 입증했습니다.",
    supportTitle: "고객지원",
    supportDesc:
      "PUMASY AI에 대한 문의사항이나 기술 지원이 필요하시면 언제든 연락주세요.",
    formNameLabel: "이름",
    formEmailLabel: "이메일",
    formCompanyLabel: "회사명",
    formMessageLabel: "문의내용",
    submitBtn: "문의하기",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    footer: "© 2025 PUMASY AI all rights reserved.",
    // 청약 관련 텍스트
    stockTitle: "온라인 청약 신청",
    stockDesc: "PUMASY AI 주식 청약을 위한 정보를 입력해주세요.",
    stockInfoTitle: "청약 안내",
    stockImportantNotice: "※ 중요 안내사항",
    stockAllocationRule: "• 배정 기준은 신청 순이 아니라 청약금 입금 순입니다.",
    stockDepositAccount:
      "• 청약금 납입계좌 : KB국민은행 001590-17-579175 (주)푸마시",
    stockDepositConfirm:
      "• 청약 증거금 입금 시 반드시 본사로 전화하여 입금확인 부탁드립니다.",
    stockCompanyName: "상 호 :",
    stockCompanyValue: "주식회사 푸마시",
    stockTotalShares: "회사가 발행할 주식의 총수 :",
    stockTotalSharesValue: "100,000,000주",
    stockParValue: "1주의 금액(액면가) :",
    stockParValueValue: "금 500원",
    stockNewShares: "신주식의 종류와 수 :",
    stockNewSharesValue: "보통주 14,000주",
    stockIssuePrice: "신주식의 발행가액 :",
    stockIssuePriceValue: "1주 15,000원",
    stockDepositRecipient: "청약증거금 납입처 :",
    stockDepositRecipientValue: "한국증권금융",
    stockFinancialInstitution: "납입 받을 금융기관 :",
    stockFinancialInstitutionValue: "KB국민은행 디지털밸리종금센터",
    stockSubscriptionPeriod: "청약일 :",
    stockSubscriptionPeriodValue: "2025년 11월 18일 ~ 2025년 11월 21일",
    stockDepositHours: "입금 가능 시간 :",
    stockDepositHoursValue: "09:00 ~ 17:20",
    stockRefundDate: "환불 및 주금 납입기일:",
    stockRefundDateValue: "2025년 11월 25일",
    stockResolutionDate: "신주식 발행결의일 :",
    stockResolutionDateValue: "2025년 11월 03일",
    stockSubscriptionMethod: "주식의 인수 방법 :",
    stockSubscriptionMethodValue:
      "정관 제9조 2항(일반공모증자의 방식으로 신주를 발행하는 경우)에 따라 일반(기존 주주 포함)에서 인수 하기로 한다.",
    stockQuantity: "주식 수량",
    stockName: "이름",
    stockAccountHolder: "예금주명",
    stockAccountNumber: "계좌번호",
    stockBankName: "은행명",
    stockId: "주민등록번호",
    stockIdFront: "주민등록번호 앞자리",
    stockIdBack: "주민등록번호 뒷자리",
    stockPhone: "전화번호",
    stockAddress: "주소",
    stockAgreement: "개인정보 수집 및 이용에 동의합니다.",
    stockSubmit: "청약 신청",
    stockSending: "전송 중...",
    stockSuccessMessage: "주식 정보가 성공적으로 전송되었습니다!",
    stockErrorMessage:
      "주식 정보 전송 중 오류가 발생했습니다. 다시 시도해주세요.",
    stockFillAllFields: "모든 필드를 입력해주세요.",
  },
  EN: {
    logo: "PUMASY AI",
    menu: [
      "About",
      "Technology",
      "News",
      "Support",
      "Online Investment",
      "Notice",
    ],
    subLeft: "Growth",
    subRight: "Singularity",
    desc: "We lead the innovation of AI technology through continuous research and development.",
    aboutTitle: "About Us",
    aboutOverviewTitle: "Business Overview",
    aboutOverviewDesc:
      "In response to the rapidly evolving AI era, PUMASY AI secures differentiated competitiveness through a user-participation-based LLM learning structure. By selecting and training high-quality data generated by rewarded users, we verify and improve hallucinations via collective intelligence and prevent information distortion from iterative learning. We pursue sustainable platform growth through an IP-based economic ecosystem and an ad revenue-sharing model.",
    aboutDesc1:
      "PUMASY AI is a leading AI platform company that innovates the future through next-generation artificial intelligence technology.",
    aboutDesc2:
      "We aim to help businesses and individuals achieve better results by leveraging the power of AI.",
    visionTitle: "Vision",
    visionDesc: "Making human life richer through artificial intelligence",
    missionTitle: "Mission",
    missionDesc:
      "Solving business and daily problems with innovative AI solutions",
    valuesTitle: "Values",
    valuesDesc: "Reliability, innovation, and user-centered approach",
    techTitle: "Technology",
    techDesc1:
      "PUMASY AI develops its own sophisticated Core Deep Learning model that is far more refined than existing machine learning and deep learning technologies, aiming to secure a competitive advantage in the AI singularity race and achieve sustainable, controllable AI development.",
    techProductsTitle: "Key Products & Services",
    techProductsDesc:
      "Pumasy's products and services include model development tools, hallucination handling systems, differentiated learning pipelines, integrated content studios, economic ecosystems, and B2B data assetization.",
    product1Title: "Model Development Tools",
    product1Desc:
      "High-productivity AI development tools specialized for Pumasy, including data preprocessing, fine-tuning modules, and deployment modules.",
    product2Title: "Hallucination Handling System",
    product2Desc:
      "A sophisticated verification system: SearchGPT summarizes top search results with ads removed, and ReportCopilot enables user-driven validation to detect and correct hallucinations.",
    product3Title: "Differentiated Learning Pipeline",
    product3Desc:
      "Improve inference performance with a general-inference hybrid structure by collecting and selecting diverse user-generated content through a reward system and training on top samples.",
    product4Title: "Integrated Content Studio",
    product4Desc:
      "Develops unified tools for image, video, and music production to enable creators to produce monetizable content easily.",
    product5Title: "Economic Ecosystem & IP Management",
    product5Desc:
      "Introduce creator ownership, guarantee rewards and copyrights, and enable economic circulation within the platform.",
    product6Title: "B2B Solutions & Partnership Marketing",
    product6Desc:
      "Scale partner businesses globally with Pumasy's core data and tailored AI services, and monetize through cloud-based big data assetization.",
    nlpTitle: "Instruction - Following",
    nlpDesc: "Generating responses that align with user intent",
    cvTitle: "Reward Model",
    cvDesc:
      "Reflects the feedback score of the generated response to the person",
    mlTitle: "Machine Learning Platform",
    mlDesc:
      "Anyone can easily access AI models in NoCode/LowCode environments.",
    automlTitle: "AutoML",
    automlDesc:
      "Develop optimal models quickly with automated machine learning.",
    newsTitle: "News",
    newsDesc: "Check out the latest news and updates from PUMASY AI.",
    news1Title: "PUMASY AI Launches Next-Generation Multimodal AI Platform",
    news1Desc:
      "Unveiled innovative AI solutions that integrate text, image, and voice processing.",
    news2Title: "Global Partnership Expansion",
    news2Desc:
      "Signed strategic partnerships for entry into North American and European markets.",
    news3Title: "AI Safety Certification Acquired",
    news3Desc:
      "Proved reliability by obtaining certification that meets international AI safety standards.",
    supportTitle: "Customer Support",
    supportDesc:
      "Please contact us anytime if you have any questions about PUMASY AI or need technical support.",
    formNameLabel: "Name",
    formEmailLabel: "Email",
    formCompanyLabel: "Company",
    formMessageLabel: "Message",
    submitBtn: "Submit",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    footer: "© 2025 PUMASY AI all rights reserved.",
    // 청약 관련 텍스트
    stockTitle: "Online Investment",
    stockDesc:
      "Please enter your information for PUMASY AI stock subscription.",
    stockInfoTitle: "Subscription Information",
    stockImportantNotice: "※ Important Notice",
    stockAllocationRule:
      "• Allocation is based on deposit order, not application order.",
    stockDepositAccount:
      "• Subscription deposit account: KB Bank 001590-17-559799 (Pumasy Co.)",
    stockDepositConfirm:
      "• Please call the headquarters to confirm deposit when making subscription deposit.",
    stockCompanyName: "Company Name:",
    stockCompanyValue: "Pumasy Co., Ltd.",
    stockTotalShares: "Total shares to be issued:",
    stockTotalSharesValue: "100,000,000 shares",
    stockParValue: "Par value per share:",
    stockParValueValue: "KRW 500",
    stockNewShares: "Type and number of new shares:",
    stockNewSharesValue: "66,600 common shares",
    stockIssuePrice: "Issue price per share:",
    stockIssuePriceValue: "KRW 15,000",
    stockDepositRecipient: "Subscription deposit recipient:",
    stockDepositRecipientValue: "Korea Securities Finance",
    stockFinancialInstitution: "Financial institution:",
    stockFinancialInstitutionValue: "KB Bank Digital Valley Branch",
    stockSubscriptionPeriod: "Subscription period:",
    stockSubscriptionPeriodValue: "March 19, 2025 ~ March 21, 2025",
    stockDepositHours: "Deposit hours:",
    stockDepositHoursValue: "09:00 ~ 17:20 (March 21: 09:00 ~ 16:00)",
    stockRefundDate: "Refund and payment date:",
    stockRefundDateValue: "March 25, 2025",
    stockResolutionDate: "New share issuance resolution date:",
    stockResolutionDateValue: "March 11, 2025",
    stockSubscriptionMethod: "Share subscription method:",
    stockSubscriptionMethodValue:
      "New shares will be subscribed by the general public (including existing shareholders) in accordance with Article 9, Paragraph 2 of the Articles of Incorporation (when issuing new shares through general public offering).",
    stockQuantity: "Stock Quantity",
    stockName: "Name",
    stockAccountHolder: "Account Holder",
    stockAccountNumber: "Account Number",
    stockBankName: "Bank Name",
    stockId: "ID",
    stockIdFront: "First 6 digits of ID",
    stockIdBack: "Last 7 digits of ID",
    stockPhone: "Phone Number",
    stockAddress: "Address",
    stockAgreement:
      "I agree to the collection and use of personal information.",
    stockSubmit: "Submit Application",
    stockSending: "Sending...",
    stockSuccessMessage: "Stock information has been successfully submitted!",
    stockErrorMessage:
      "An error occurred while submitting stock information. Please try again.",
    stockFillAllFields: "Please fill in all fields.",
  },
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [currentLang, setCurrentLang] = useState<Language>("KOR");

  return (
    <LanguageContext.Provider
      value={{ currentLang, setCurrentLang, translations }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
